import { useCallback, useMemo } from 'react';
import { DataTable } from '@components/DataTable';
import { useContainerHeight } from '@components/DataTable/hooks';
import { DataTableColumn, SelectionModel } from '@components/DataTable/types';
import { sanitizeHtml } from '@helper/string.utils';
import { Avatar, Box, IconButton, Typography, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BrandItem } from './types';

export interface BrandsDataTableProps {
    brands: BrandItem[];
    totalDocs: number;
    loading: boolean;
    isLoadingMore?: boolean;
    page: number;
    rowsPerPage: number;
    selectionModel: SelectionModel;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onSelectionModelChange: (model: SelectionModel) => void;
    onDeleteRow: (id: string) => void;
    onScrollEnd?: () => void;
}

const BrandsDataTable = ({
    brands,
    totalDocs,
    loading,
    isLoadingMore,
    page,
    rowsPerPage,
    selectionModel,
    onPageChange,
    onPageSizeChange,
    onSelectionModelChange,
    onDeleteRow,
    onScrollEnd,
}: BrandsDataTableProps) => {
    const navigate = useNavigate();
    const { containerRef, containerHeight } = useContainerHeight();

    const handleEdit = useCallback(
        (brand: BrandItem) => {
            navigate(`/brands/${brand._id}/detail`);
        },
        [navigate],
    );

    const columns = useMemo<DataTableColumn<BrandItem>[]>(
        () => [
            {
                id: 'brandName',
                label: 'Nom de la marque',
                wrap: true,
                sticky: true,
                renderCell: ({ row }) => (
                    <Stack flexDirection="row" alignItems="center" gap={2} sx={{ minWidth: 0, width: '100%' }}>
                        <Avatar
                            src={row.brandLogo?.url ?? '/default-logo.png'}
                            alt={row.brandName}
                            sx={{ width: 36, height: 36, bgcolor: 'primary.main', mt: 0.25 }}>
                            {row.brandName?.[0]?.toUpperCase()}
                        </Avatar>
                        <Stack sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                onClick={() => handleEdit(row)}
                                sx={{
                                    fontSize: 14,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    width: '100%',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}>
                                {row.brandName ? sanitizeHtml(row.brandName) : '-'}
                            </Typography>
                            {row.pro?.commercialName && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {row.pro.commercialName}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                ),
            },
            {
                id: 'photos',
                label: 'Photos',
                align: 'center',
                minWidth: 40,
                renderCell: ({ row }) => row.brandPhotos?.length ?? 0,
            },
            {
                id: 'videos',
                label: 'Vidéos',
                align: 'center',
                minWidth: 40,
                renderCell: ({ row }) => row.brandVideos?.length ?? 0,
            },
            {
                id: 'isFeatured',
                label: 'En vedette',
                align: 'center',
                minWidth: 40,
                renderCell: ({ row }) => (row.isFeatured ? 'Oui' : 'Non'),
            },
            {
                id: 'sector',
                label: 'Secteur',
                minWidth: 120,
                renderCell: ({ row }) => row.sector?.sectorName ?? '-',
            },
            {
                id: 'description',
                label: 'Description',
                minWidth: 400,
                wrap: true,
                renderCell: ({ row }) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            width: '100%',
                        }}>
                        {row.description ? sanitizeHtml(row.description) : '-'}
                    </Typography>
                ),
            },
            {
                id: 'createdAt',
                label: 'Créé le',
                minWidth: 100,
                renderCell: ({ row }) =>
                    row.createdAt
                        ? new Date(row.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '-',
            },
            {
                id: 'actions',
                label: 'Actions',
                align: 'center',
                width: 80,
                renderCell: ({ row }) => (
                    <>
                        <IconButton size="small" onClick={() => handleEdit(row)}>
                            <i className="fi fi-br-pencil" style={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => onDeleteRow(row._id)}>
                            <i className="fi fi-br-trash" style={{ fontSize: 14 }} />
                        </IconButton>
                    </>
                ),
            },
        ],
        [handleEdit, onDeleteRow],
    );

    return (
        <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <DataTable
                stickyHeader
                rows={brands}
                columns={columns}
                loading={loading}
                isLoadingMore={isLoadingMore}
                onScrollEnd={onScrollEnd}
                checkboxSelection
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
                height={containerHeight}
                pagination={{
                    currentPage: page + 1,
                    pageSize: rowsPerPage,
                    totalRows: totalDocs,
                    onPageChange: newPage => onPageChange(newPage - 1),
                    onPageSizeChange,
                    pageSizeOptions: [10, 20, 50, 100],
                }}
            />
        </Box>
    );
};

export default BrandsDataTable;
